import { BUG_BENCH_PATH } from '@/constants/bug-bench';
import { getPublicMarketingPaths } from '@/constants/marketing-routes';
import { DESKTOP_TRAY_PATH } from '@/lib/desktop-tray-window';

const BASE_PUBLIC_PATHS = [
  '/ingest',
  '/install', // CLI install script endpoint
  '/login',
  '/sign-in',
  '/sign-up',
  '/vercel', // Vercel integration sign-in page
  '/auth',
  '/auth/callback',
  '/api/auth/github-redirect',
  '/auth/desktop-callback',
  '/cli',
  '/docs',
  '/.well-known/agent-card.json', // A2A agent discovery document
  '/.well-known/mcp/manifest.json', // MCP server discovery manifest
  '/schema', // Public JSON schema files used by editor integrations
  '/manifest.webmanifest',
  '/survey', // PMF survey from review footers; the signed token in its query is the credential
  '/api/files/[...segments]', // Public file access
  '/wikis', // Public wiki directory page
  '/wikis/[owner]/[repo]', // Public wiki viewer page
  '/wikis/search', // Public wiki search page
  '/api/public/wiki/search/stream', // Public wiki search streaming API
  '/inside-ai-coding/embed', // Public chart embeds for git-AI data report (subpaths are per-chart embeds)
  // Bug Bench: the leaderboard and everything under /bugbench/ (pages, share
  // images, data files) is public, so an unknown path 404s instead of
  // redirecting to sign-in. Prefix matching stops at a path segment, so
  // /bugbenchmark stays private.
  BUG_BENCH_PATH,
  `${BUG_BENCH_PATH}.md`, // Markdown mirror of the leaderboard
  '/llms.txt', // Index for answer engines, linking the Bug Bench files
] as const;

// Paths that must match exactly (not prefix-matched), so future subpaths don't inherit public access.
const EXACT_PUBLIC_PATHS = [
  // Desktop tray popover shell. It shows its own signed-out state instead of redirecting the small
  // popover window to sign-in, and loads the queue only through authenticated APIs.
  DESKTOP_TRAY_PATH,
  // IndexNow ownership key; search engines fetch it signed out.
  '/indexnow-key.txt',
] as const;

export const PUBLIC_PATHS = Array.from(
  new Set([...getPublicMarketingPaths(), ...BASE_PUBLIC_PATHS]),
);

export const PUBLIC_PR_PAGE_ROUTE = '/pr/[owner]/[repo]/pull/[number]' as const;

const OPTIONAL_CATCH_ALL_PLACEHOLDER = '__OPTIONAL_CATCH_ALL__';
const CATCH_ALL_PLACEHOLDER = '__CATCH_ALL__';
const SEGMENT_PLACEHOLDER = '__SEGMENT__';

function escapeRegexLiteral(value: string): string {
  return value.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
}

export function matchesRoutePattern(
  pathname: string,
  path: string,
  options: { allowSubpaths?: boolean } = {},
): boolean {
  const { allowSubpaths = true } = options;

  if (path === '/') {
    return pathname === '/';
  }

  if (path.includes('[')) {
    const regexPattern = escapeRegexLiteral(
      path
        .replace(/\[\[\.\.\.[^\]]+\]\]/g, OPTIONAL_CATCH_ALL_PLACEHOLDER)
        .replace(/\[\.\.\.[^\]]+\]/g, CATCH_ALL_PLACEHOLDER)
        .replace(/\[[^\]]+\]/g, SEGMENT_PLACEHOLDER),
    )
      .replaceAll(OPTIONAL_CATCH_ALL_PLACEHOLDER, '.*')
      .replaceAll(CATCH_ALL_PLACEHOLDER, '.+')
      .replaceAll(SEGMENT_PLACEHOLDER, '[^/]+');
    const suffixPattern = allowSubpaths ? '(/.*)?' : '';
    const regex = new RegExp(`^${regexPattern}${suffixPattern}$`);
    return regex.test(pathname);
  }

  return allowSubpaths ? pathname === path || pathname.startsWith(`${path}/`) : pathname === path;
}

export function isBasePublicPath(pathname: string): boolean {
  // Normalize one trailing slash: next.config sets `skipTrailingSlashRedirect`,
  // so Next never redirects '/desktop/tray/' to '/desktop/tray' and serves
  // both; the exact allow-list must match either instead of bouncing to
  // /sign-in. (/bugbench, /agents, /standards, and /wikis paths get their own
  // 308 in the proxy before auth.)
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if ((EXACT_PUBLIC_PATHS as readonly string[]).includes(normalized)) {
    return true;
  }
  return PUBLIC_PATHS.some((path) => matchesRoutePattern(normalized, path));
}

export function isPotentialPublicPrPagePath(pathname: string): boolean {
  return matchesRoutePattern(pathname, PUBLIC_PR_PAGE_ROUTE, {
    allowSubpaths: false,
  });
}
